import React, {useEffect, useMemo, useState} from 'react';
import {
    Card,
    CardContent,
    Stack,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Link,
    IconButton,
    Box,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import {InputField, DatePicker, RangeDatePicker, AutocompleteField, OverlayMask, MoneyField} from '@/components/common';
import {handleFormValidate} from '@/utils/formValidateUtil';
import {NumberField} from '@/components/common/NumberField';
import RepaymentScheduleDrawer from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import {LoanScheduleResponse, LoanScheduleRequest, calcBankLoanByOcr, OcrLoanCalcResult} from '@/services/auditService/auditLoanCalculatorService';
import {BankLoanRow} from '@/services/auditService/interface/wp-row';
import {BankService} from '@/services/manageService/bankService';
import type {
    BankLoanSums,
    BankLoanScheduleSummaryTrace,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import {
    mergeBankLoanSumsPreserveChanges,
    mergeBankLoanSumsFromScheduleRecalc,
    isBankLoanScheduleUserRecalc,
    normalizeBankLoanSumsForDetailDisplay,
    resolveBankLoanLastYoaTotal,
    type BankLoanSumPeriodWindow,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanSumCalc';
import LoanSummaryCard, {type LoanSummaryCell} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanSummaryCard';
import {
    emptyBankLoanSums,
    resolveBalanceYoa,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanSummarySums';
import type {BankLoanLastYoaColumnDisplay} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanMultiYearSave';
import {
    toNumber,
    addMonths,
    formatDDMMYYYY,
    parseDDMMYYYY,
    formatYYYYMMDD,
    calculateMonthlyPayment,
    formatNumber,
    formatNumber4Decimals,
    formatMMYYYY,
    parseMMYYYY,
} from '@/utils/dataUtils';
export interface BankLoneDetailsProps {
    yoa: string;
    lastYoa: string;
    currencySymbol?: string; // e.g. HKD
    // Notify parent when the repayment drawer opens/closes (for layout animation)
    onDrawerStateChange?: (open: boolean) => void;
    // Callback when user confirms (validation passed). Receives the committed loan payload.
    onConfirm?: (data: Partial<BankLoanRow>) => void;
    // Called when dialog is closed without confirming
    onCancel?: () => void;
    // How to render the trigger to open the calculator
    triggerVariant?: 'link' | 'icon';
    // Hide trigger; use with controlled `open` (e.g. Add Bank Loan flow)
    hideTrigger?: boolean;
    // Controlled open state
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    // When true, do not sync via onDataChange while editing; only emit on confirm
    deferDataChangeUntilConfirm?: boolean;
    // Annual ID to fetch period ranges
    annualId?: number;
    // Initial data to populate the form (for editing existing loans)
    initialData?: Partial<BankLoanRow> | null;
    // Real-time callback when form data changes
    onDataChange?: (data: Partial<BankLoanRow>) => void;
    readOnly?: boolean;
    /**
     * MULTI later YOA Tab: lock core loan fields; Summary current-YOA *Change
     * and Early repayment date remain editable; last-YOA Summary column is display-only.
     * Unaffected by full `readOnly`.
     */
    lockCoreFields?: boolean;
    /**
     * MULTI later Tab: last-YOA Summary amounts from prior YOA current column
     * (xxxChange if not null, else calculated). When set, overrides local last-YOA sums for display.
     */
    lastYoaColumnDisplay?: BankLoanLastYoaColumnDisplay | null;
    // 1.3: Hide lastYoa column when firstYearAudit=1
    hideLastYoa?: boolean;
    // 2.1: Incorporation date for validation
    inCorpDate?: string | null;
    // Period end date for checking if loan is in future
    periodTo?: string | null;
    /**
     * MULTI YOA Tab: schedule currentYear / lastYear windows for this Tab.
     * Forwarded to RepaymentScheduleDrawer so highlights match the active Tab, not engagement annual.
     */
    periodWindow?: BankLoanSumPeriodWindow | null;
    /** Notify parent to show OCR processing overlay */
    onOcrProcessingStart?: () => void;
    /** Notify parent that backend OCR data has been received */
    onOcrProcessingComplete?: () => void;
    /** Notify parent to hide OCR processing overlay on failure */
    onOcrProcessingError?: () => void;
    /** Whether OCR processing overlay is active (disables upload button) */
    ocrProcessingOpen?: boolean;
}

const loanTypeOptions = [
    'Mortgage loan',
    'SME loan',
    'Revolving loan',
];

const securityOptions = [
    "The bank loans were secured by personal guarantees given by the Company's directors and guarantee issued by the Government of the Hong Kong Special Administrative",
    'The bank and trust receipt loans are secured by the leasehold properties of the company (note7) and the directors together with guarantees from the directors for unlimited',
];

interface BankLoanForm {
    bank: string;
    loanNo: string;
    loanAmt: string;
    ratePct: string;
    loanType: string;
    terms: number | null;
    firstRepay: string;
    /** Early repayment date DD/MM/YYYY — schedule stops after this date. */
    earlyRepay: string;
    /** Interest free from MM/YYYY. */
    interestFreeFrom: string;
    /** Interest free to MM/YYYY. */
    interestFreeTo: string;
    /** Principal free from MM/YYYY. */
    principalFreeFrom: string;
    /** Principal free to MM/YYYY. */
    principalFreeTo: string;
    security: string | null;
    currentTotalInput: string;
}

const initialForm: BankLoanForm = {
    bank: '',
    loanNo: '',
    loanAmt: '',
    ratePct: '',
    loanType: '',
    terms: null,
    firstRepay: '',
    earlyRepay: '',
    interestFreeFrom: '',
    interestFreeTo: '',
    principalFreeFrom: '',
    principalFreeTo: '',
    security: null,
    currentTotalInput: '',
};

/** Map persisted BankLoanRow free-period fields onto form (MM/YYYY). */
const freePeriodFromInitial = (data: Partial<BankLoanRow> | null | undefined) => ({
    interestFreeFrom: data?.interestFreeFrom || '',
    interestFreeTo: data?.interestFreeTo || '',
    principalFreeFrom: data?.principalFreeFrom || '',
    principalFreeTo: data?.principalFreeTo || '',
});

const bankLoanRequiredFields = ['bank', 'loanNo', 'loanAmt', 'ratePct', 'loanType', 'terms', 'firstRepay', 'currentTotalInput'];

/** Default Loan Calculator paper width — matches the previous `maxWidth="sm"` + fullWidth paper (600px). */
const DIALOG_BASE_WIDTH_PX = 600;

/**
 * Fixed horizontal chrome around the Bank row (the widest rendered child):
 * label minWidth 160 + Stack spacing 16 + DialogContent padding 24*2 + Card outline 1*2 + CardContent padding 16*2.
 */
const BANK_ROW_FIXED_PX = 160 + 16 + 48 + 2 + 32;

/** DialogContent 24*2 + outer Card outline 1*2 + CardContent 16*2 — around Summary wrap. */
const SUMMARY_OUTER_CHROME_PX = 48 + 2 + 32;

/** Measure text width in px with the small-size input font (same approach as AutocompleteField). */
const measureTextPx = (text: string): number => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return 0;
        ctx.font = '400 14px Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';
        return Math.ceil(ctx.measureText(text).width);
    } catch {
        return 0;
    }
};

const emptySums = (): BankLoanSums => emptyBankLoanSums();

export default function BankLoanDetails({
                                            yoa, lastYoa,
                                            currencySymbol = 'HKD',
                                            onDrawerStateChange,
                                            onConfirm,
                                            onCancel,
                                            triggerVariant = 'link',
                                            hideTrigger = false,
                                            open: openProp,
                                            onOpenChange,
                                            deferDataChangeUntilConfirm = false,
                                            annualId,
                                            initialData,
                                            onDataChange,
                                            readOnly = false,
                                            lockCoreFields = false,
                                            lastYoaColumnDisplay = null,
                                            hideLastYoa = false,
                                            inCorpDate = null,
                                            periodTo = null,
                                            periodWindow = null,
                                            onOcrProcessingStart,
                                            onOcrProcessingComplete,
                                            onOcrProcessingError,
                                            ocrProcessingOpen = false,
                                        }: BankLoneDetailsProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const cardRef = React.useRef<HTMLDivElement | null>(null);
    const isMountedRef = React.useRef(false);
    const onDataChangeRef = React.useRef(onDataChange);
    const isFirstRender = React.useRef(true);
    const isOpenControlled = openProp !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const calcOpen = isOpenControlled ? !!openProp : uncontrolledOpen;
    const setCalcOpen = React.useCallback((next: boolean) => {
        if (!isOpenControlled) setUncontrolledOpen(next);
        onOpenChange?.(next);
    }, [isOpenControlled, onOpenChange]);
    const [form, setForm] = useState<BankLoanForm>(() => {
        if (!initialData) return {...initialForm};
        return {
            bank: initialData.bank || '',
            loanNo: initialData.loanNo || '',
            loanAmt: formatNumber(initialData.loanAmt || 0),
            ratePct: formatNumber4Decimals(initialData.ratePct || 0),
            loanType: initialData.loanType || '',
            terms: initialData.terms || null,
            firstRepay: initialData.firstRepay || '',
            earlyRepay: initialData.earlyRepay || '',
            ...freePeriodFromInitial(initialData),
            security: initialData.security || null,
            currentTotalInput: formatNumber(initialData.currentTotalInput || 0),
        };
    });
    const [formError, setFormError] = useState<Record<string, string>>({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    /** Summary calc hover → RepaymentScheduleDrawer period/column outline. */
    const [summaryTrace, setSummaryTrace] = useState<BankLoanScheduleSummaryTrace | null>(null);
    const [scheduleData, setScheduleData] = useState<LoanScheduleResponse | null>(null);
    const [shiftX, setShiftX] = useState<number>(0);
    // Width of the RepaymentScheduleDrawer Paper (0 when closed)
    const [drawerWidth, setDrawerWidth] = useState<number>(0);
    const [scheduleRequest, setScheduleRequest] = useState<LoanScheduleRequest | null>(null);
    // 2.1: Snackbar state for validation warnings
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Bank dictionary options for the Bank dropdown (same source as BankWp "Name of Bank")
    const [bankNameOptions, setBankNameOptions] = useState<string[]>([]);

    // Load admin_bank dictionary when the calculator dialog opens
    useEffect(() => {
        if (!calcOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const page = await BankService.getList(1, 500);
                if (cancelled) return;
                const names = (page?.records || [])
                    .filter((b) => b.enabled !== 0)
                    .map((b) => String(b.nameEn || b.nameCn || b.bankCode || '').trim())
                    .filter(Boolean);
                setBankNameOptions(Array.from(new Set(names)));
            } catch {
                if (cancelled) return;
                setBankNameOptions([]);
                setSnackbarMessage('Failed to load bank dictionary');
                setSnackbarOpen(true);
            }
        })();
        return () => { cancelled = true; };
    }, [calcOpen]);

    // Widest bank text across dictionary options + current value → shared single-line width (ch),
    // same model as BankWp `bankNameColWidthCh`. Drives both the Bank input and the dialog paper width.
    const bankNameColWidthCh = useMemo(() => {
        let maxLen = String('Select bank').length;
        for (const name of bankNameOptions) {
            const n = String(name || '').length;
            if (n > maxLen) maxLen = n;
        }
        const cur = String(form.bank || '').length;
        if (cur > maxLen) maxLen = cur;
        // +4 for popup icon / padding; floor keeps short columns usable
        return Math.max(16, maxLen + 4);
    }, [bankNameOptions, form.bank]);

    // Dialog paper width adapts to the widest rendered child (Bank row OR Summary split MoneyFields).
    const [dialogPaperWidthPx, setDialogPaperWidthPx] = useState(DIALOG_BASE_WIDTH_PX);
    const summaryWrapRef = React.useRef<HTMLDivElement | null>(null);
    const [summaryNeededPx, setSummaryNeededPx] = useState(0);

    useEffect(() => {
        const compute = () => {
            const chPx = measureTextPx('0') || 8;
            // Direct canvas measure of the widest option as a safety net when the ch estimate is tight
            let widestText = 'Select bank';
            for (const name of bankNameOptions) {
                if (String(name || '').length > widestText.length) widestText = name;
            }
            const widestTextPx = measureTextPx(widestText) || 0;
            const inputNeededPx = Math.max(bankNameColWidthCh * chPx, widestTextPx + 44);
            const bankNeeded = BANK_ROW_FIXED_PX + inputNeededPx;
            const maxW = Math.max(DIALOG_BASE_WIDTH_PX, window.innerWidth * 0.9);
            const needed = Math.max(DIALOG_BASE_WIDTH_PX, bankNeeded, summaryNeededPx);
            setDialogPaperWidthPx(Math.round(Math.min(needed, maxW)));
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, [bankNameColWidthCh, bankNameOptions, summaryNeededPx]);

    // OCR upload states
    const [ocrScheduleData, setOcrScheduleData] = useState<LoanScheduleResponse | null>(null);
    const [isOcrMode, setIsOcrMode] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    // Sums calculated by RepaymentScheduleDrawer
    // Backend save may bleed *Change into base xxx — strip equal bleed until schedule refills base.
    const [sums, setSums] = useState<BankLoanSums>(() =>
        normalizeBankLoanSumsForDetailDisplay(initialData?.sums || emptySums()),
    );
    /** True after schedule/OCR merge wrote calculated base; parent sync must not re-strip equal Change. */
    const scheduleBaseReadyRef = React.useRef(false);

    // Handle OCR file upload
    const handleOcrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        // Reset file input so same file can be re-selected
        event.target.value = '';

        onOcrProcessingStart?.();

        try {
            const result: OcrLoanCalcResult = await calcBankLoanByOcr(file);

            const scheduleRows = result?.schedule?.schedule || [];
            if (!scheduleRows.length) {
                throw new Error(
                    'No repayment schedule rows recognized. Please upload a loan repayment schedule PDF.',
                );
            }

            // Prefer a dictionary bank name that matches OCR bank text (e.g. CITIC)
            const ocrBank = String(result.bank || '').trim();
            const matchedBank =
                bankNameOptions.find((n) => n.toLowerCase() === ocrBank.toLowerCase())
                || bankNameOptions.find((n) => {
                    const a = n.toLowerCase();
                    const b = ocrBank.toLowerCase();
                    return a && b && (a.includes(b) || b.includes(a) || b.includes('citic') && a.includes('citic'));
                })
                || ocrBank;

            // Populate form fields with OCR result
            const newForm: BankLoanForm = {
                bank: matchedBank,
                loanNo: result.loanNo || '',
                loanAmt: formatNumber(result.loanAmt || 0),
                ratePct: formatNumber4Decimals(result.ratePct || 0),
                loanType: result.loanType || '',
                terms: result.terms || null,
                firstRepay: result.firstRepay || '',
                earlyRepay: form.earlyRepay || '',
                interestFreeFrom: form.interestFreeFrom || '',
                interestFreeTo: form.interestFreeTo || '',
                principalFreeFrom: form.principalFreeFrom || '',
                principalFreeTo: form.principalFreeTo || '',
                security: form.security,
                currentTotalInput: form.currentTotalInput,
            };
            setForm(newForm);

            // Store OCR schedule data for read-only display
            setOcrScheduleData(result.schedule);
            setIsOcrMode(true);

            // Open drawer to display schedule
            setDrawerOpen(true);
            onDrawerStateChange?.(true);

            // Open calc dialog if not already open
            if (!calcOpen) setCalcOpen(true);

            onOcrProcessingComplete?.();
        } catch (e: any) {
            onOcrProcessingError?.();
            setSnackbarMessage(e?.message || 'OCR parsing failed');
            setSnackbarOpen(true);
        }
    };

    const lastRepay = useMemo(() => {
        if (!form.firstRepay || !form.terms || form.terms <= 0) return '';
        const base = parseDDMMYYYY(form.firstRepay);
        if (!base) return '';
        const d = addMonths(base, Math.max(form.terms - 1, 0));
        return formatDDMMYYYY(d);
    }, [form.firstRepay, form.terms]);

    /**
     * Free-period months must be strictly after First and before Last (YearMonth).
     * Bounds stay DD/MM/YYYY day-01 for DatePicker minDate/maxDate month compare.
     */
    const freePeriodBounds = useMemo(() => {
        const first = parseDDMMYYYY(form.firstRepay);
        const last = parseDDMMYYYY(lastRepay);
        if (!first || !last) return {min: null as string | null, max: null as string | null};
        const minD = addMonths(new Date(first.getFullYear(), first.getMonth(), 1), 1);
        const maxD = addMonths(new Date(last.getFullYear(), last.getMonth(), 1), -1);
        if (minD.getTime() > maxD.getTime()) return {min: null, max: null};
        return {min: formatDDMMYYYY(minD), max: formatDDMMYYYY(maxD)};
    }, [form.firstRepay, lastRepay]);

    const instalment = useMemo(() => {
        return calculateMonthlyPayment(toNumber(form.loanAmt), toNumber(form.ratePct), form.terms ?? 0);
    }, [form.loanAmt, form.ratePct, form.terms]);

    /** MULTI later Tab: last-YOA Summary *Change must stay display-only. */
    const isLastYoaSumChangeField = (field: keyof BankLoanSums) =>
        field === 'interestLastYoaChange'
        || field === 'currentLastYoaChange'
        || field === 'balanceLastYoaChange';

    // Handle sums changes for editable fields (current-YOA *Change stays editable under lockCoreFields)
    // Empty → null (not 0): 0 is a valid override and would zero BankLoan "Repayment during the year".
    const handleSumsChange = (field: keyof BankLoanSums, value: string) => {
        if (readOnly) return;
        if (lockCoreFields && isLastYoaSumChangeField(field)) return;
        suppressParentFromScheduleRef.current = false;
        const trimmed = String(value ?? '').trim();
        const numValue = trimmed === '' ? null : toNumber(trimmed);
        setSums((prev) => {
            const next = {...prev, [field]: numValue};
            // B/F wire ← resolved Total lastYoa (Change ?? calc)
            if (field === 'balanceLastYoaChange') {
                const resolved = numValue != null
                    ? numValue
                    : (prev.balanceLastYoa ?? 0);
                setForm((f) => ({...f, currentTotalInput: formatNumber(resolved)}));
            }
            return next;
        });
    };

    // Handle cancel edit — explicit null so sanitize/save clears DB *Change (not omit / not 0)
    const handleSumsCancel = (field: keyof BankLoanSums) => {
        if (readOnly) return;
        if (lockCoreFields && isLastYoaSumChangeField(field)) return;
        suppressParentFromScheduleRef.current = false;
        setSums((prev) => {
            const next = {...prev, [field]: null};
            if (field === 'balanceLastYoaChange') {
                setForm((f) => ({...f, currentTotalInput: formatNumber(prev.balanceLastYoa ?? 0)}));
            }
            return next;
        });
    };

    // 记录上一次的 initialData id，用于判断是否需要同步数据
    const prevInitialDataIdRef = React.useRef<number | undefined>(initialData?.id);
    // Ref to track current sums for deep comparison (avoids infinite loops from bidirectional sync)
    const currentSumsRef = React.useRef(sums);
    currentSumsRef.current = sums;
    // When sums are synced from parent, suppress back-propagation to parent to avoid ping-pong updates
    const syncingSumsFromParentRef = React.useRef(false);
    /**
     * Opening Detail / init calcBankLoanSchedule must not rewrite BankLoan table.
     * Cleared when user edits form / Summary *Change, or when Drawer user-recalc pushes sums.
     */
    const suppressParentFromScheduleRef = React.useRef(true);
    const prevCalcOpenRef = React.useRef(false);

    // Re-hydrate form + sums when dialog opens so B/F (lastYoa Total = currentTotalInput) matches parent.
    useEffect(() => {
        const justOpened = calcOpen && !prevCalcOpenRef.current;
        prevCalcOpenRef.current = calcOpen;
        if (!justOpened || !initialData) return;

        suppressParentFromScheduleRef.current = true;
        scheduleBaseReadyRef.current = false;
        setForm({
            bank: initialData.bank || '',
            loanNo: initialData.loanNo || '',
            loanAmt: formatNumber(initialData.loanAmt || 0),
            ratePct: formatNumber4Decimals(initialData.ratePct || 0),
            loanType: initialData.loanType || '',
            terms: initialData.terms || null,
            firstRepay: initialData.firstRepay || '',
            earlyRepay: initialData.earlyRepay || '',
            ...freePeriodFromInitial(initialData),
            security: initialData.security || null,
            currentTotalInput: formatNumber(initialData.currentTotalInput || 0),
        });
        if (initialData.sums) {
            syncingSumsFromParentRef.current = true;
            setSums(normalizeBankLoanSumsForDetailDisplay(initialData.sums));
        }
    }, [calcOpen, initialData]);

    // Sync currentTotalInput from parent when it changes without loan id change (e.g. MULTI roll) and dialog closed.
    useEffect(() => {
        if (calcOpen || !initialData) return;
        const next = formatNumber(initialData.currentTotalInput || 0);
        setForm((prev) => (prev.currentTotalInput === next ? prev : {...prev, currentTotalInput: next}));
    }, [calcOpen, initialData?.currentTotalInput, initialData]);

    // 只在 initialData.id 变化时（即加载不同的贷款数据）同步 form 状态
    useEffect(() => {
        const currentId = initialData?.id;
        const prevId = prevInitialDataIdRef.current;

        // 只有当 id 变化时才同步数据（首次加载或切换到不同的贷款）
        if (currentId !== prevId && initialData) {
            prevInitialDataIdRef.current = currentId;

            // 更新 form 状态
            setForm({
                bank: initialData.bank || '',
                loanNo: initialData.loanNo || '',
                loanAmt: formatNumber(initialData.loanAmt || 0),
                ratePct: formatNumber4Decimals(initialData.ratePct || 0),
                loanType: initialData.loanType || '',
                terms: initialData.terms || null,
                firstRepay: initialData.firstRepay || '',
                earlyRepay: initialData.earlyRepay || '',
                ...freePeriodFromInitial(initialData),
                security: initialData.security || null,
                currentTotalInput: formatNumber(initialData.currentTotalInput || 0),
            });
            // 同步 sums（id 变化代表切换到不同的贷款）
            scheduleBaseReadyRef.current = false;
            if (initialData.sums) {
                syncingSumsFromParentRef.current = true;
                setSums(normalizeBankLoanSumsForDetailDisplay(initialData.sums));
            }
        }
    }, [initialData]);

    // Sync sums from parent when initialData.sums changes
    // (e.g. from parent's RepaymentScheduleDrawer recalculation or backend data reload)
    useEffect(() => {
        const parentSums = initialData?.sums;
        if (!parentSums) return;

        // Open-init: schedule refreshed local calc-side but parent write is suppressed.
        // Do not clobber fresher split-left bases with stale parent sums.
        if (scheduleBaseReadyRef.current && suppressParentFromScheduleRef.current) {
            return;
        }

        const cur = currentSumsRef.current;
        // Deep compare to avoid infinite loops from bidirectional sync
        const hasChanged =
            parentSums.interestYoa !== cur.interestYoa ||
            parentSums.currentLastYoa !== cur.currentLastYoa ||
            parentSums.interestLastYoa !== cur.interestLastYoa ||
            parentSums.currentYoa !== cur.currentYoa ||
            parentSums.nonCurrentYoa !== cur.nonCurrentYoa ||
            parentSums.interestNext !== cur.interestNext ||
            parentSums.interestYoaChange !== cur.interestYoaChange ||
            parentSums.interestLastYoaChange !== cur.interestLastYoaChange ||
            parentSums.currentYoaChange !== cur.currentYoaChange ||
            parentSums.currentLastYoaChange !== cur.currentLastYoaChange ||
            parentSums.nonCurrentYoaChange !== cur.nonCurrentYoaChange ||
            parentSums.prevFinalBalance !== cur.prevFinalBalance ||
            parentSums.currFinalBalance !== cur.currFinalBalance ||
            parentSums.editedInstalments !== cur.editedInstalments ||
            (parentSums as BankLoanSums).balanceLastYoa !== cur.balanceLastYoa ||
            parentSums.balanceLastYoaChange !== cur.balanceLastYoaChange;

        if (hasChanged) {
            syncingSumsFromParentRef.current = true;
            // Before schedule refill: strip Change-bleed into base. After: keep equal Change==calc.
            setSums(
                scheduleBaseReadyRef.current
                    ? parentSums
                    : normalizeBankLoanSumsForDetailDisplay(parentSums),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.sums]);

    // 2.1: Validate First repayment date against incorporation date
    useEffect(() => {
        if (!inCorpDate || !form.firstRepay) return;

        const firstRepayDate = parseDDMMYYYY(form.firstRepay);
        const incorpDate = parseDDMMYYYY(inCorpDate);

        if (firstRepayDate && incorpDate && firstRepayDate < incorpDate) {
            // Auto-correct to corpDate
            const correctedDate = formatDDMMYYYY(incorpDate);
            setForm(prev => ({...prev, firstRepay: correctedDate}));
            setSnackbarMessage('First repayment date must after incorp date!');
            setSnackbarOpen(true);
        }
    }, [form.firstRepay, inCorpDate]);

    const handleFormChange = (field: keyof BankLoanForm, value: any) => {
        // MULTI later YOA: only earlyRepay (and Summary *Change via handleSumsChange) may change
        if (lockCoreFields && field !== 'earlyRepay') return;
        suppressParentFromScheduleRef.current = false;
        setForm(prev => ({...prev, [field]: value}));
        handleFormValidate(field, value, bankLoanRequiredFields, setFormError);
    };

    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    useEffect(() => {
        isMountedRef.current = true;
        const timer = setTimeout(() => {
            isFirstRender.current = false;
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    // Notify parent after commit (never call onDataChange inside setState updaters — that updates parent during child render)
    useEffect(() => {
        if (deferDataChangeUntilConfirm) return;
        // Always-mounted trigger: never push while dialog closed
        if (!calcOpen) return;
        if (!isMountedRef.current || isFirstRender.current) return;
        if (syncingSumsFromParentRef.current) {
            syncingSumsFromParentRef.current = false;
            return;
        }
        if (suppressParentFromScheduleRef.current) return;
        onDataChangeRef.current?.({
            bank: form.bank,
            loanNo: form.loanNo,
            loanAmt: toNumber(form.loanAmt),
            ratePct: toNumber(form.ratePct),
            loanType: form.loanType,
            terms: form.terms || 0,
            firstRepay: form.firstRepay,
            earlyRepay: form.earlyRepay || null,
            interestFreeFrom: form.interestFreeFrom || null,
            interestFreeTo: form.interestFreeTo || null,
            principalFreeFrom: form.principalFreeFrom || null,
            principalFreeTo: form.principalFreeTo || null,
            lastRepay: lastRepay,
            instalment: toNumber(instalment),
            // B/F ← Total lastYoa resolved (Change ?? calc)
            currentTotalInput: resolveBankLoanLastYoaTotal(sums, toNumber(form.currentTotalInput)),
            sums,
            security: form.security,
        });
    }, [
        calcOpen,
        form.bank, form.loanNo, form.loanAmt, form.ratePct, form.loanType, form.terms,
        form.firstRepay, form.earlyRepay, form.currentTotalInput, form.security,
        form.interestFreeFrom, form.interestFreeTo, form.principalFreeFrom, form.principalFreeTo,
        sums, lastRepay, instalment, deferDataChangeUntilConfirm,
    ]);

    // Build schedule request when inputs change
    useEffect(() => {
        const amt = toNumber(form.loanAmt);
        const rate = toNumber(form.ratePct);
        const n = form.terms ?? 0;
        const first = parseDDMMYYYY(form.firstRepay);

        if (!amt || !rate || !n || !first) {
            setScheduleRequest(null);
            return;
        }

        const tenorYears = n / 12;
        const firstRepaymentDate = formatYYYYMMDD(first);

        setScheduleRequest({
            loanAmount: amt,
            tenorYears,
            annualInterestRatePercent: rate,
            firstRepaymentDate,
            interestFreeFrom: form.interestFreeFrom || null,
            interestFreeTo: form.interestFreeTo || null,
            principalFreeFrom: form.principalFreeFrom || null,
            principalFreeTo: form.principalFreeTo || null,
        });
    }, [
        form.loanAmt, form.ratePct, form.terms, form.firstRepay,
        form.interestFreeFrom, form.interestFreeTo, form.principalFreeFrom, form.principalFreeTo,
    ]);

    // Auto-open drawer when schedule request is ready
    useEffect(() => {
        if (!calcOpen || !scheduleRequest) return;
        const timer = setTimeout(() => {
            setDrawerOpen(true);
            onDrawerStateChange?.(true);
        }, 300);
        return () => clearTimeout(timer);
    }, [calcOpen, scheduleRequest]);

    const nameValue = (name: string, control: React.ReactNode) => (
        <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{minWidth: 160}}>{name}</Typography>
            <Stack sx={{flex: 1}}>{control}</Stack>
        </Stack>
    );

    /**
     * Month-year range via RangeDatePicker precision=month (value MM/YYYY).
     * from after First repayment month; to before Last repayment month.
     */
    const monthRangeRow = (
        fromField: 'interestFreeFrom' | 'principalFreeFrom',
        toField: 'interestFreeTo' | 'principalFreeTo',
    ) => {
        const fromErr = formError[fromField];
        const toErr = formError[toField];
        const apply = (field: typeof fromField | typeof toField, mmYyyy: string | null) => {
            const mm = (mmYyyy || '').trim();
            if (!mm) {
                handleFormChange(field, '');
                setFormError((prev) => ({...prev, [field]: ''}));
                return;
            }
            const picked = parseMMYYYY(mm);
            if (!picked) return;
            const min = parseDDMMYYYY(freePeriodBounds.min);
            const max = parseDDMMYYYY(freePeriodBounds.max);
            if (!min || !max) {
                handleFormChange(field, '');
                setFormError((prev) => ({
                    ...prev,
                    [field]: 'Set First repayment date and terms first',
                }));
                return;
            }
            const pm = picked.getFullYear() * 12 + picked.getMonth();
            const minM = min.getFullYear() * 12 + min.getMonth();
            const maxM = max.getFullYear() * 12 + max.getMonth();
            if (pm < minM || pm > maxM) {
                handleFormChange(field, '');
                setFormError((prev) => ({
                    ...prev,
                    [field]: 'Must be after First repayment date and before Last repayment date',
                }));
                return;
            }
            handleFormChange(field, formatMMYYYY(picked));
            setFormError((prev) => ({...prev, [field]: ''}));
        };
        return (
            <Box>
                <RangeDatePicker
                    precision="month"
                    from={form[fromField] || null}
                    to={form[toField] || null}
                    onChangeFrom={(v) => apply(fromField, v)}
                    onChangeTo={(v) => apply(toField, v)}
                    minDate={freePeriodBounds.min}
                    maxDate={freePeriodBounds.max}
                    disabled={coreLocked}
                    size="small"
                    errorFrom={!!fromErr}
                    errorTo={!!toErr}
                />
                {(!!fromErr || !!toErr) && (
                    <Typography variant="caption" color="error">{fromErr || toErr}</Typography>
                )}
            </Box>
        );
    };

    /** MoneyField.suffixValue rejects null — map cleared Change to undefined for display. */
    const changeSuffix = (v: number | null | undefined): number | undefined =>
        v == null ? undefined : v;

    /** Hover Summary calc (split left) → trace matching schedule period/column; derived cells → no-op. */
    const clearScheduleTrace = React.useCallback(() => {
        setSummaryTrace(null);
    }, []);

    const bindScheduleTrace = React.useCallback((trace: BankLoanScheduleSummaryTrace | null) => {
        return (hovering: boolean) => {
            // Mouse left the calc value → drop red outline + Σ in RepaymentScheduleDrawer
            if (!hovering) {
                setSummaryTrace(null);
                return;
            }
            if (!trace) return;
            setDrawerOpen(true);
            onDrawerStateChange?.(true);
            setSummaryTrace(trace);
        };
    }, [onDrawerStateChange]);

    // Compute horizontal shift so the Dialog clears the RepaymentScheduleDrawer (left-anchored)
    // and stays within the viewport regardless of browser size.
    React.useLayoutEffect(() => {
        const calcShift = () => {
            if (!cardRef.current) return 0;
            // Prefer the Dialog Paper width (more accurate than the inner Card)
            const paper = cardRef.current.closest('.MuiDialog-paper') as HTMLElement | null;
            const dialogWidth = (paper ? paper.getBoundingClientRect().width : cardRef.current.getBoundingClientRect().width);
            const viewportWidth = window.innerWidth;
            const viewportCenter = viewportWidth / 2;
            const margin = 16; // safety gap between drawer and dialog
            // Dialog is centered; its default left edge sits at viewportCenter - dialogWidth/2.
            // We need: (viewportCenter - dialogWidth/2) + shiftX >= drawerWidth + margin
            const required = drawerWidth + margin - (viewportCenter - dialogWidth / 2);
            // Don't let the dialog spill past the right viewport edge
            const maxShift = Math.max(0, viewportCenter - dialogWidth / 2 - margin);
            if (required <= 0) return 0;
            return Math.round(Math.min(required, maxShift));
        };

        const apply = () => {
            if (drawerOpen) {
                setShiftX(calcShift());
            } else {
                setShiftX(0);
            }
        };

        apply();
        // Recompute on resize while drawer is open; also when the paper width itself changes
        // (dialog adapts to the widest rendered child) so the shift stays correct.
        const onResize = () => {
            if (drawerOpen) setShiftX(calcShift());
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [drawerOpen, drawerWidth, dialogPaperWidthPx]);

    // Ensure drawer closes (no leftover open state) if component unmounts
    React.useEffect(() => {
        return () => setDrawerOpen(false);
    }, []);

    // Close drawer when dialog closes and notify parent
    React.useEffect(() => {
        if (!calcOpen && drawerOpen) {
            setDrawerOpen(false);
            setSummaryTrace(null);
            onDrawerStateChange?.(false);
        }
    }, [calcOpen]);

    // Notify parent on drawer state change
    React.useEffect(() => {
        if (!drawerOpen) setSummaryTrace(null);
        onDrawerStateChange?.(drawerOpen);
    }, [drawerOpen]);

    // Check if loan is in the future (firstPaymentDate > periodTo)
    const isFutureLoan = useMemo(() => {
        if (!form.firstRepay || !periodTo) return false;
        try {
            const firstRepayDate = parseDDMMYYYY(form.firstRepay);
            const periodToDate = parseDDMMYYYY(periodTo);
            if (firstRepayDate && periodToDate) {
                return firstRepayDate > periodToDate;
            }
        } catch (e) {
            console.warn('Failed to parse dates for future loan check:', e);
        }
        return false;
    }, [form.firstRepay, periodTo]);

    // Summary Card natural width → dialog paper (split MoneyField content must not clip).
    React.useLayoutEffect(() => {
        const el = summaryWrapRef.current;
        if (!el || !calcOpen) return;
        const measure = () => {
            const table = el.querySelector('table');
            const contentW = Math.max(el.scrollWidth, table?.scrollWidth ?? 0);
            setSummaryNeededPx(SUMMARY_OUTER_CHROME_PX + contentW);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const table = el.querySelector('table');
        if (table) ro.observe(table);
        return () => ro.disconnect();
    }, [
        calcOpen,
        sums.interestYoa, sums.interestYoaChange,
        sums.interestLastYoa, sums.interestLastYoaChange,
        sums.currentYoa, sums.currentYoaChange,
        sums.currentLastYoa, sums.currentLastYoaChange,
        sums.nonCurrentYoa, sums.nonCurrentYoaChange,
        sums.balanceLastYoa, sums.balanceLastYoaChange,
        form.currentTotalInput, hideLastYoa, currencySymbol, isFutureLoan,
    ]);

    /** MULTI later YOA: core form locked; current-YOA Summary *Change + earlyRepay still editable unless full readOnly. */
    const coreLocked = readOnly || lockCoreFields;
    const summaryChangeEditable = !readOnly;
    /**
     * MULTI later Tab only: last-YOA Summary plain display (schedule-derived).
     * Single Year / MULTI first Tab keep legacy split MoneyField behavior.
     */
    const multiLaterLastYoaDisplay = lockCoreFields;
    /** Schedule blue/green windows (Multi year SummaryCard last-YOA column). */
    const lastYoaInterestDisplay = sums.interestLastYoaChange != null
        ? sums.interestLastYoaChange
        : (sums.interestLastYoa || 0);
    const lastYoaPrincipalLt1Display = sums.currentLastYoaChange != null
        ? sums.currentLastYoaChange
        : (sums.currentLastYoa || 0);
    const lastYoaTotalCalc = sums.balanceLastYoa ?? 0;
    const lastYoaTotalDisplay = resolveBankLoanLastYoaTotal(sums, lastYoaTotalCalc);
    const lastYoaPrincipalGt1Display = lastYoaTotalDisplay - lastYoaPrincipalLt1Display;

    // Keep B/F (currentTotalInput) = Total lastYoa resolved (Change ?? calc); schedule never overwrites *Change.
    useEffect(() => {
        if (lockCoreFields || hideLastYoa) return;
        if (sums.balanceLastYoa == null && sums.balanceLastYoaChange == null) return;
        const next = formatNumber(resolveBankLoanLastYoaTotal(sums, 0));
        setForm((prev) => (prev.currentTotalInput === next ? prev : {...prev, currentTotalInput: next}));
    }, [sums.balanceLastYoa, sums.balanceLastYoaChange, lockCoreFields, hideLastYoa]);

    const buildLoanPayload = (): Partial<BankLoanRow> => ({
        bank: form.bank,
        loanNo: form.loanNo,
        loanAmt: toNumber(form.loanAmt),
        ratePct: toNumber(form.ratePct),
        loanType: form.loanType,
        terms: form.terms || 0,
        firstRepay: form.firstRepay,
        earlyRepay: form.earlyRepay || null,
        interestFreeFrom: form.interestFreeFrom || null,
        interestFreeTo: form.interestFreeTo || null,
        principalFreeFrom: form.principalFreeFrom || null,
        principalFreeTo: form.principalFreeTo || null,
        lastRepay: lastRepay,
        instalment: toNumber(instalment),
        currentTotalInput: resolveBankLoanLastYoaTotal(sums, toNumber(form.currentTotalInput)),
        sums,
        security: form.security,
    });

    /** Nested under WP Dialog: blur + skip restore so closing does not leave focus under aria-hidden. */
    const blurActiveElement = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    /** Prevent action buttons from keeping focus while the nested Modal applies aria-hidden. */
    const preventButtonFocus = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleClose = () => {
        blurActiveElement();
        setDrawerOpen(false);
        onDrawerStateChange?.(false);
        setCalcOpen(false);
        onCancel?.();
    };

    const handleConfirm = () => {
        const isDefined = (v: unknown) => v !== undefined && v !== null;
        // Interest-only override: allow Confirm without required form fields
        const hasInterestChange =
            isDefined(sums.interestYoaChange)
            || (!hideLastYoa && isDefined(sums.interestLastYoaChange));
            const hasOtherSummaryChange =
            isDefined(sums.currentYoaChange)
            || isDefined(sums.currentLastYoaChange)
            || isDefined(sums.nonCurrentYoaChange)
            || isDefined(sums.balanceLastYoaChange);
        // WHY: initialData 缺省金额时 formatNumber(0) 会写成 "0.00"，不能当作用户已填表单
        const hasFormInput =
            !!String(form.bank || '').trim()
            || !!String(form.loanNo || '').trim()
            || !!String(form.loanType || '').trim()
            || !!(form.terms && form.terms > 0)
            || !!String(form.firstRepay || '').trim()
            || toNumber(form.loanAmt) > 0
            || toNumber(form.ratePct) > 0
            || (!hideLastYoa && (
                isDefined(sums.balanceLastYoaChange)
                || (sums.balanceLastYoa != null && sums.balanceLastYoa !== 0)
                || toNumber(form.currentTotalInput) > 0
            ));
        const skipRequiredValidation = hasInterestChange && !hasOtherSummaryChange && !hasFormInput;

        if (!skipRequiredValidation) {
            // Validate all required inputs are non-empty
            const newErrors: Record<string, string> = {};
            const requireStr = (val: string, key: string, label: string) => {
                if (!val || String(val).trim() === '') newErrors[key] = `${label} is required`;
            };
            requireStr(form.bank, 'bank', 'Bank');
            requireStr(form.loanNo, 'loanNo', 'Loan ac no');
            requireStr(form.loanAmt, 'loanAmt', 'Loan amt');
            requireStr(form.ratePct, 'ratePct', 'Interest Rate');
            requireStr(form.loanType, 'loanType', 'Loan Type');
            if (!form.terms || form.terms <= 0) newErrors['terms'] = 'Terms is required';
            requireStr(form.firstRepay, 'firstRepay', 'First repayment date');
            // lastRepay is derived but still ensure existence
            if (!lastRepay) newErrors['lastRepay'] = 'Last repayment date is required';
            // Total lastYoa: calc and/or Change; skip when hideLastYoa (firstAudit=1)
            if (!hideLastYoa) {
                const hasTotalLastYoa =
                    isDefined(sums.balanceLastYoaChange)
                    || sums.balanceLastYoa != null
                    || toNumber(form.currentTotalInput) > 0;
                if (!hasTotalLastYoa) {
                    newErrors['currentTotalInput'] = 'Total (last year) is required';
                }
            }

            setFormError(newErrors);
            if (Object.keys(newErrors).length > 0) return;
        } else {
            setFormError({});
        }

        const payload = buildLoanPayload();
        // When not deferred, keep live sync; when deferred, parent only receives data here
        if (!deferDataChangeUntilConfirm) {
            onDataChangeRef.current?.(payload);
        }

        // Blur + close before onConfirm: parent may set controlled `open` false immediately.
        blurActiveElement();
        setDrawerOpen(false);
        onDrawerStateChange?.(false);
        setCalcOpen(false);
        try {
            onConfirm?.(payload);
        } catch {
        }
    }

    const lastSplitOrPlain = (
        splitValue: number | undefined,
        changeField: keyof BankLoanSums,
        hover: ((hovering: boolean) => void) | undefined,
        plainValue: number,
        extra?: Partial<LoanSummaryCell>,
    ): LoanSummaryCell => {
        if (multiLaterLastYoaDisplay) {
            return {mode: 'plainMoney', value: plainValue, onCalcHoverChange: hover, ...extra};
        }
        return {
            mode: 'split',
            value: splitValue,
            suffixValue: changeSuffix(sums[changeField] as number | null | undefined),
            suffixEditable: summaryChangeEditable,
            onSuffixChange: summaryChangeEditable
                ? (n) => handleSumsChange(changeField, n == null ? '' : String(n))
                : undefined,
            onEditCancel: summaryChangeEditable ? () => handleSumsCancel(changeField) : undefined,
            onCalcHoverChange: hover,
            ...extra,
        };
    };

    const yoaSplit = (
        value: number | undefined,
        changeField: keyof BankLoanSums,
        hover?: (hovering: boolean) => void,
    ): LoanSummaryCell => {
        if (isFutureLoan) return {mode: 'dash'};
        return {
            mode: 'split',
            value,
            suffixValue: changeSuffix(sums[changeField] as number | null | undefined),
            suffixEditable: summaryChangeEditable,
            onSuffixChange: summaryChangeEditable
                ? (n) => handleSumsChange(changeField, n == null ? '' : String(n))
                : undefined,
            onEditCancel: summaryChangeEditable ? () => handleSumsCancel(changeField) : undefined,
            onCalcHoverChange: hover,
        };
    };

    const SummaryCard = (
        <LoanSummaryCard
            yoa={yoa}
            lastYoa={lastYoa}
            hideLastYoa={hideLastYoa}
            wrapRef={summaryWrapRef}
            onMouseLeave={clearScheduleTrace}
            interestYoa={yoaSplit(sums.interestYoa, 'interestYoaChange', bindScheduleTrace({period: 'curr', column: 'interest', mode: 'sum'}))}
            interestLastYoa={lastSplitOrPlain(
                sums.interestLastYoa,
                'interestLastYoaChange',
                bindScheduleTrace({period: 'last', column: 'interest', mode: 'sum'}),
                lastYoaInterestDisplay,
            )}
            currentYoa={yoaSplit(sums.currentYoa, 'currentYoaChange', bindScheduleTrace({period: 'next', column: 'principal', mode: 'sum'}))}
            currentLastYoa={lastSplitOrPlain(
                sums.currentLastYoa,
                'currentLastYoaChange',
                bindScheduleTrace({period: 'curr', column: 'principal', mode: 'sum'}),
                lastYoaPrincipalLt1Display,
            )}
            nonCurrentYoa={yoaSplit(sums.nonCurrentYoa, 'nonCurrentYoaChange', bindScheduleTrace({period: 'next', column: 'balance', mode: 'last'}))}
            nonCurrentLastYoa={multiLaterLastYoaDisplay
                ? {mode: 'plainMoney', value: lastYoaPrincipalGt1Display}
                : {mode: 'plainNumber', value: lastYoaPrincipalGt1Display}}
            balanceYoa={isFutureLoan
                ? {mode: 'dash'}
                : {mode: 'plainNumber', value: resolveBalanceYoa(sums)}}
            balanceLastYoa={lastSplitOrPlain(
                lastYoaTotalCalc,
                'balanceLastYoaChange',
                sums.balanceLastYoa != null || !multiLaterLastYoaDisplay
                    ? bindScheduleTrace({period: 'last', column: 'balance', mode: 'last'})
                    : undefined,
                lastYoaTotalDisplay,
                {errorText: formError.currentTotalInput},
            )}
        />
    );

    return (
        <>
            {/* Single shared overlay mask rendered via Portal; sits below Dialog and Drawer */}
            <OverlayMask open={calcOpen}
                         onClick={() => {
                             // Do nothing on blank area click; closing is controlled via explicit actions
                         }}
            />
            {/* Trigger to open calculator dialog */}
            {!hideTrigger && !readOnly && (triggerVariant === 'icon' ? (
                <IconButton size="small" color="primary" onClick={() => setCalcOpen(true)} aria-label="Edit bank loan">
                    <EditIcon fontSize="small"/>
                </IconButton>
            ) : (
                <Link component="button" type="button" variant="body2" onClick={() => setCalcOpen(true)}
                      sx={{lineHeight: 1}}>
                    Calculator
                </Link>
            ))}

            <Dialog
                sx={{
                    zIndex: (theme) => theme.zIndex.modal,
                    transition: 'transform 300ms ease',
                    transform: `translateX(${shiftX}px)`
                }}
                open={calcOpen}
                onClose={handleClose}
                // Paper width adapts to the widest rendered child (Bank row) — see dialogPaperWidthPx
                maxWidth={false}
                disableEnforceFocus
                disableRestoreFocus
                closeAfterTransition={false}
                hideBackdrop
                slotProps={{
                    paper: {
                        'data-no-maximize': 'true',
                        sx: {
                            width: `${dialogPaperWidthPx}px`,
                            maxWidth: '90vw',
                        },
                    } as any,
                }}
            >
                <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1.5}}>
                    <Typography component="span" variant="h6">Loan Calculator</Typography>
                    {!readOnly && !lockCoreFields && (
                        <Box sx={{display: 'inline-flex', alignItems: 'center'}}>
                            <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => fileInputRef.current?.click()}
                                aria-label="Upload loan schedule for OCR"
                                disabled={ocrProcessingOpen}
                                title="Upload loan schedule for OCR"
                            >
                                <UploadFileIcon fontSize="small"/>
                            </IconButton>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                style={{display: 'none'}}
                                onChange={handleOcrUpload}
                            />
                        </Box>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <div ref={containerRef}>
                        <Card
                            ref={cardRef}
                            variant="outlined"
                        >
                            <CardContent>
                                {/* Summary always shown; values fill in after schedule calc */}
                                {SummaryCard}
                                <Divider sx={{mb: 1}}/>

                                <Stack spacing={1.25}>
                                    {nameValue(
                                        'Bank',
                                        <Box>
                                            {coreLocked ? (
                                                <Typography variant="body2" align="right" sx={{pr: 2}}>
                                                    {form.bank || '-'}
                                                </Typography>
                                            ) : (
                                            <AutocompleteField
                                                options={bankNameOptions}
                                                value={form.bank || null}
                                                onChange={(v) => handleFormChange('bank', v || '')}
                                                commitOnBlur
                                                align="right"
                                                size="small"
                                                /* No multiline wrap (singleLine disables the overflow→multiline switch).
                                                   singleLineFit fills the dialog content area — the input's rendered
                                                   width never exceeds BankLoanDetail itself; the dialog paper adapts to
                                                   the widest bank name (see dialogPaperWidthPx) so the value stays on
                                                   one line. */
                                                fullWidth
                                                singleLine
                                                singleLineFit
                                                forcePopupIcon
                                                fullWidthOptions
                                                placeholder="Select bank"
                                                disablePortal={false}
                                                slotProps={{
                                                    popper: {
                                                        placement: 'bottom-start',
                                                        disablePortal: false,
                                                        sx: (theme: any) => ({zIndex: theme.zIndex.modal + 3}),
                                                    },
                                                } as any}
                                            />
                                            )}
                                            {!!formError.bank && (
                                                <Typography variant="caption"
                                                            color="error">{formError.bank}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Loan ac no',
                                        <Box>
                                            <InputField
                                                size="small"
                                                align="right"
                                                value={form.loanNo}
                                                display={coreLocked}
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    // Filter out Chinese characters (Unicode range: \u4e00-\u9fff)
                                                    const filteredValue = value.replace(/[\u4e00-\u9fff]/g, '');
                                                    handleFormChange('loanNo', filteredValue);
                                                }}
                                                placeholder="Enter loan account number"
                                                error={!!formError.loanNo}
                                            />
                                            {!!formError.loanNo && (
                                                <Typography variant="caption"
                                                            color="error">{formError.loanNo}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Loan amt',
                                        <Box>
                                            <MoneyField
                                                editable={!coreLocked}
                                                align="right"
                                                compact
                                                value={form.loanAmt}
                                                onChangeDisplay={(display) => handleFormChange('loanAmt', display)}
                                                currencySymbol={currencySymbol}
                                            />
                                            {!!formError.loanAmt && (
                                                <Typography variant="caption"
                                                            color="error">{formError.loanAmt}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Interest Rate',
                                        <Box>
                                            {coreLocked ? (
                                                <Typography variant="body2" align="right" sx={{width: '100%', pr: 2}}>
                                                    {form.ratePct ? `${form.ratePct}%` : '-'}
                                                </Typography>
                                            ) : (
                                                <NumberField
                                                    type="float"
                                                    decimalPlaces={4}
                                                    editable
                                                    align="right"
                                                    value={form.ratePct}
                                                    onChangeDisplay={(v) => handleFormChange('ratePct', v)}
                                                    suffix="%"
                                                    compact
                                                    placeholder="0.0000"
                                                />
                                            )}
                                            {!!formError.ratePct && (
                                                <Typography variant="caption"
                                                            color="error">{formError.ratePct}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Loan Type',
                                        <Box>
                                            {coreLocked ? (
                                                <Typography variant="body2" align="right" sx={{width: '100%', pr: 2}}>
                                                    {form.loanType || '-'}
                                                </Typography>
                                            ) : (
                                                <AutocompleteField
                                                    align="right"
                                                    size="small"
                                                    options={loanTypeOptions}
                                                    value={form.loanType || null}
                                                    onChange={(v) => handleFormChange('loanType', v || '')}
                                                    disablePortal={false}
                                                    slotProps={{popper: {sx: (theme: any) => ({zIndex: theme.zIndex.modal + 3})}} as any}
                                                    placeholder="Select loan type"
                                                    fullWidth
                                                />
                                            )}
                                            {!!formError.loanType && (
                                                <Typography variant="caption"
                                                            color="error">{formError.loanType}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Instalment',
                                        <Typography variant="body2" align="right" sx={{width: '100%', pr: 2}}>
                                            {currencySymbol} {instalment || '-'}
                                        </Typography>
                                    )}
                                    {nameValue(
                                        'Terms',
                                        <Box>
                                            {coreLocked ? (
                                                <Typography variant="body2" align="right" sx={{width: '100%', pr: 2}}>
                                                    {form.terms ?? '-'}
                                                </Typography>
                                            ) : (
                                                <NumberField
                                                    type="integer"
                                                    editable
                                                    align="right"
                                                    value={form.terms ?? ''}
                                                    onChangeNumber={(v) => handleFormChange('terms', v)}
                                                    compact
                                                    placeholder="Number of months"
                                                />
                                            )}
                                            {!!formError.terms && (
                                                <Typography variant="caption"
                                                            color="error">{formError.terms}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'First repayment date',
                                        <Box>
                                            <DatePicker
                                                value={form.firstRepay || null}
                                                onChange={(v: any) => handleFormChange('firstRepay', v || '')}
                                                size="small"
                                                display={coreLocked}
                                            />
                                            {!!formError.firstRepay && (
                                                <Typography variant="caption"
                                                            color="error">{formError.firstRepay}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Early repayment date',
                                        <Box>
                                            <DatePicker
                                                value={form.earlyRepay || null}
                                                onChange={(v: any) => handleFormChange('earlyRepay', v || '')}
                                                size="small"
                                                display={readOnly}
                                                minDate={form.firstRepay || undefined}
                                                maxDate={lastRepay || undefined}
                                                placeholder="Optional"
                                            />
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Interest free period',
                                        monthRangeRow('interestFreeFrom', 'interestFreeTo'),
                                    )}
                                    {nameValue(
                                        'Principal free period',
                                        monthRangeRow('principalFreeFrom', 'principalFreeTo'),
                                    )}
                                    {nameValue(
                                        'Last repayment date',
                                        <Box>
                                            <DatePicker
                                                value={lastRepay || null}
                                                onChange={() => {
                                                }}
                                                size="small"
                                                display
                                            />
                                            {!!formError.lastRepay && (
                                                <Typography variant="caption"
                                                            color="error">{formError.lastRepay}</Typography>
                                            )}
                                        </Box>
                                    )}
                                    {nameValue(
                                        'Security',
                                        coreLocked ? (
                                            <Typography variant="body2" align="right" sx={{pr: 2}}>
                                                {form.security || '-'}
                                            </Typography>
                                        ) : (
                                        <AutocompleteField
                                            align='right'
                                            size="small"
                                            options={securityOptions}
                                            value={form.security}
                                            onChange={(v) => handleFormChange('security', v)}
                                            disablePortal={false}
                                            slotProps={{popper: {sx: (theme: any) => ({zIndex: theme.zIndex.modal + 3})}} as any}
                                            placeholder="Select security"
                                            fullWidth
                                        />
                                        )
                                    )}
                                </Stack>
                            </CardContent>
                            {/* Drawer mounted alongside to share same overlay; interactive concurrently */}
                            <RepaymentScheduleDrawer
                                open={drawerOpen}
                                onClose={() => {
                                    setDrawerOpen(false);
                                    setSummaryTrace(null);
                                    onDrawerStateChange?.(false);
                                }}
                                loanType="bank"
                                annualId={annualId}
                                scheduleRequest={isOcrMode ? null : scheduleRequest}
                                directScheduleData={isOcrMode ? ocrScheduleData : null}
                                readOnly={isOcrMode || lockCoreFields}
                                earlyRepay={form.earlyRepay || null}
                                periodWindow={periodWindow}
                                summaryTrace={summaryTrace}
                                onDataLoaded={(data) => {
                                    setScheduleData(data.scheduleData);
                                    if (!('interestYoa' in data.sums) && !('interestCurr' in data.sums)) return;
                                    const incoming = data.sums as BankLoanSums;
                                    // User recalc includes schedule override keys; init calc does not.
                                    const fromUserRecalc = isBankLoanScheduleUserRecalc(incoming);
                                    const hasSavedScheduleOverrides = !!(
                                        sums.prevFinalBalance != null
                                        || sums.currFinalBalance != null
                                        || sums.editedInstalments
                                    );
                                    // Raw init (no recalculated rows) while saved overrides exist:
                                    // keep Summary finals — raw schedule bases would be wrong.
                                    // One-shot / user recalc already applied overrides into sums →
                                    // must refresh all YOA split-left bases (Interest / <1yr / >1yr)
                                    // so Card matches hover Σ.
                                    if (
                                        hasSavedScheduleOverrides
                                        && !fromUserRecalc
                                        && !data.appliedRecalculatedSchedule
                                    ) {
                                        scheduleBaseReadyRef.current = true;
                                        return;
                                    }
                                    if (fromUserRecalc) {
                                        suppressParentFromScheduleRef.current = false;
                                    }
                                    scheduleBaseReadyRef.current = true;
                                    if (suppressParentFromScheduleRef.current) {
                                        syncingSumsFromParentRef.current = true;
                                    }
                                    setSums((prev) => (
                                        fromUserRecalc
                                            ? mergeBankLoanSumsFromScheduleRecalc(prev, incoming)
                                            : mergeBankLoanSumsPreserveChanges(prev, incoming)
                                    ) as BankLoanSums);
                                }}
                                initialOverrides={!isOcrMode && !lockCoreFields && (sums.prevFinalBalance != null || sums.currFinalBalance != null || sums.editedInstalments) ? {
                                    prevFinalBalance: sums.prevFinalBalance,
                                    currFinalBalance: sums.currFinalBalance,
                                    editedInstalments: sums.editedInstalments,
                                } : null}
                                onWidthChange={setDrawerWidth}
                            />
                        </Card>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onMouseDown={preventButtonFocus} onClick={handleClose}>Close</Button>
                    {!readOnly && (
                        <Button variant="contained" onMouseDown={preventButtonFocus} onClick={handleConfirm}>
                            Confirm
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* 2.1: Snackbar for validation warnings */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="warning" sx={{width: '100%'}}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
